import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Organization data type for user organizations
 */
export interface Organization {
    id: string;
    name: string;
}

/**
 * Auth store state interface.
 * Contains the authentication-related state that needs to persist across sessions.
 */
export interface AuthState {
    /** The currently selected organization ID. Persists in localStorage. */
    selectedOrgId: string | null;
    /** Organization ID from URL query param, pending validation. Persists in localStorage. */
    pendingOrgId: string | null;
    /** List of all organizations the user has access to. Persists in localStorage. */
    userOrganizations: Organization[] | null;
}

/**
 * Auth store actions interface.
 * Contains all the actions that can be performed on the auth state.
 */
export interface AuthActions {
    /**
     * Sets the selected organization ID.
     * @param orgId - The organization ID to set, or null to clear
     */
    setSelectedOrgId: (orgId: string | null) => void;
    /**
     * Clears the selected organization ID.
     */
    clearSelectedOrgId: () => void;
    /**
     * Sets the pending organization ID from URL query param.
     * @param orgId - The organization ID to set, or null to clear
     */
    setPendingOrgId: (orgId: string | null) => void;
    /**
     * Clears the pending organization ID.
     */
    clearPendingOrgId: () => void;
    /**
     * Sets the list of all organizations the user has access to.
     * @param orgs - Array of organizations or null to clear
     */
    setUserOrganizations: (orgs: Organization[] | null) => void;
    /**
     * Clears the list of user organizations.
     */
    clearUserOrganizations: () => void;
}

/**
 * Complete auth store type combining state and actions.
 */
export type AuthStore = AuthState & AuthActions;

/**
 * Zustand store for authentication state.
 *
 * Uses the persist middleware to automatically save/load state from localStorage.
 * This ensures the selected organization ID persists across page refreshes and even after logout.
 *
 * DevTools middleware is enabled in development for debugging.
 */
export const useAuthStore = create<AuthStore>()(
    devtools(
        persist(
            (set) => ({
                // Initial state
                selectedOrgId: null,
                pendingOrgId: null,
                userOrganizations: null,

                // Actions
                setSelectedOrgId: (orgId): void => {
                    set({ selectedOrgId: orgId });
                },
                clearSelectedOrgId: (): void => {
                    set({ selectedOrgId: null });
                },
                setPendingOrgId: (orgId): void => {
                    set({ pendingOrgId: orgId });
                },
                clearPendingOrgId: (): void => {
                    set({ pendingOrgId: null });
                },
                setUserOrganizations: (orgs): void => {
                    set({ userOrganizations: orgs });
                },
                clearUserOrganizations: (): void => {
                    set({ userOrganizations: null });
                },
            }),
            {
                name: 'dynoweb-auth-store',
                // Persist selectedOrgId, pendingOrgId, and userOrganizations
                partialize: (state) => ({
                    selectedOrgId: state.selectedOrgId,
                    pendingOrgId: state.pendingOrgId,
                    userOrganizations: state.userOrganizations,
                }),
            },
        ),
        { name: 'AuthStore' },
    ),
);

/**
 * Type-safe selector for the selected organization ID.
 *
 * @example
 * ```tsx
 * const orgId = useAuthStore(selectSelectedOrgId);
 * ```
 */
export const selectSelectedOrgId = (state: AuthState): string | null =>
    state.selectedOrgId;

/**
 * Type-safe selector for the user's organizations list.
 *
 * @example
 * ```tsx
 * const organizations = useAuthStore(selectUserOrganizations);
 * ```
 */
export const selectUserOrganizations = (
    state: AuthState,
): Organization[] | null => state.userOrganizations;
